import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { formatCpf } from "@brazilian-utils/brazilian-utils";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import { usePopup } from "../common/Popup";
import VagaResumoCard, {
  getPeriodoInicio,
  getPeriodoTermino,
} from "./VagaResumoCard";
import CadastrarAlunoForm from "./CadastrarAlunoForm";
import CadastrarAlunoRevisao from "./CadastrarAlunoRevisao";

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(0, 2fr);
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const BottomArea = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  width: 100%;
  margin: 24px 0 0;
  padding: 0 20px;
`;

const RightPanel = styled.div`
  background: #ffffff;
  border-top: 1px solid #e0e0e0;
  border-right: 1px solid #f3f3f3;
  border-bottom: 1px solid #f3f3f3;
  border-left: none;
  padding: 12px 24px 20px;
  min-width: 0;
`;

const PanelHeader = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #ff9f1f;
  margin-bottom: 12px;
`;

const ProgressText = styled.div`
  font-size: 15px;
  color: #555555;
  margin-bottom: 16px;
  font-weight: 600;
`;

const SlotList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: calc(100vh - 280px);
  overflow-y: auto;
  padding-right: 4px;
`;

const SlotItem = styled.div`
  border: 1px solid #dfe3e8;
  background: ${({ $filled }) => ($filled ? "#e8f5f3" : "#ffffff")};
`;

const SlotHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  gap: 12px;
`;

const SlotLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const PersonIcon = styled.span`
  font-family: "Material Symbols Outlined", sans-serif;
  font-size: 22px;
  color: #6ab8a8;
  flex-shrink: 0;
`;

const SlotTitle = styled.div`
  font-size: 15px;
  color: #555555;
  font-weight: ${({ $filled }) => ($filled ? 600 : 500)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SlotSub = styled.div`
  font-size: 13px;
  color: #777777;
`;

const AddButton = styled.button`
  background: #6ab8a8;
  color: #ffffff;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #5da89a;
  }
`;

const ExpandButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: #555555;
  font-size: 18px;
  padding: 0 4px;
`;

const SlotHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-shrink: 0;
`;

const TermoEnviadoStatus = styled.span`
  color: ${({ $sent }) => ($sent ? "#478f81" : "#b42318")};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

const SlotDetails = styled.div`
  padding: 0 14px 14px 46px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 20px;
  font-size: 14px;
  color: #555555;
`;

const DetailLabel = styled.div`
  font-weight: 700;
  color: #333333;
`;

const GrayVoltarButton = styled.button`
  background-color: #999;
  color: #ffffff;
  border: none;
  padding: 8px 24px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0px 4px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;

  &:hover {
    background-color: #888;
  }
`;

const GerarTermoButton = styled.button`
  background: #ff9f1f;
  color: #ffffff;
  border: none;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #e89010;
  }
`;

const GerarTermoCell = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
`;

const FullWidthView = styled.div``;

const CadastrarAlunosDetalhe = ({ solicitacao, onVoltar }) => {
  const popup = usePopup();
  const navigate = useNavigate();
  const [alunos, setAlunos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("lista");
  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});

  const total =
    solicitacao.quantidadeAlunosConcedidos ?? solicitacao.quantidadeAlunos ?? 0;

  const localRealizacao = solicitacao?.vaga?.estabelecimentoSaude?.nome || "-";
  const inicioEstagio = getPeriodoInicio(solicitacao?.periodos) || "-";
  const terminoEstagio = getPeriodoTermino(solicitacao?.periodos) || "-";
  const cargaHorariaTotal = solicitacao?.horasTotaisPorAluno ?? "-";

  const fetchAlunos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getAlunosEstagio(solicitacao.id);
      setAlunos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erro ao carregar alunos:", err);
    } finally {
      setLoading(false);
    }
  }, [solicitacao.id]);

  useEffect(() => {
    fetchAlunos();
  }, [fetchAlunos]);

  const slots = useMemo(() => {
    const filled = [...alunos];
    const emptyCount = Math.max(0, total - filled.length);
    const emptySlots = Array.from({ length: emptyCount }, (_, i) => ({
      id: `empty-${i}`,
      empty: true,
    }));
    return [...filled.map((a) => ({ ...a, empty: false })), ...emptySlots];
  }, [alunos, total]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleOpenForm = () => {
    setFormData(null);
    setView("form");
  };

  const handleTermo = async (aluno) => {
    if (aluno?.termoCompromissoCaminho) {
      try {
        await apiService.downloadTermoCompromissoAluno(
          aluno.id,
          aluno.termoCompromissoNome || `termo-${aluno.nome}.pdf`
        );
      } catch (err) {
        await popup.alert({
          icon: "warning",
          title: "Erro ao baixar termo assinado",
          message: err.message || "Tente novamente.",
        });
      }
      return;
    }

    try {
      const dadosEstagio = await apiService.getDadosEstagio(solicitacao.id);
      if (!dadosEstagio) {
        await popup.alert({
          icon: "warning",
          title: "Dados do estágio não preenchidos",
          message:
            "Preencha e salve os dados do estágio antes de gerar o termo de compromisso.",
          confirmText: "Preencher dados",
        });
        navigate("/ConfirmarConcessao", {
          state: {
            solicitacaoId: solicitacao.id,
            vagaId: solicitacao.vaga?.id ?? solicitacao.vagaId,
            onlyConfirmada: true,
          },
        });
        return;
      }
    } catch (err) {
      await popup.alert({
        icon: "warning",
        title: "Erro ao verificar dados do estágio",
        message: err.message || "Tente novamente.",
      });
      return;
    }

    await popup.alert({
      icon: "info",
      title: "Termo de compromisso",
      message:
        "O termo deve ser preenchido, assinado e enviado na seção 'Termo de Compromisso'.",
      confirmText: "Baixar termo",
    });

    try {
      await apiService.gerarTermoCompromissoAluno(
        aluno.id,
        `Termo de Compromisso - ${aluno.nome}.docx`
      );
    } catch (err) {
      await popup.alert({
        icon: "warning",
        title: "Erro ao baixar termo",
        message: err.message || "Tente novamente.",
      });
    }
  };

  const handleProximo = (data) => {
    setFormData(data);
    setView("revisao");
  };

  const handleSalvar = async () => {
    if (!formData) return;
    setSaving(true);
    try {
      await apiService.createAlunoEstagio(solicitacao.id, {
        nome: formData.nome.trim(),
        cpf: formData.cpf,
        dataNascimento: new Date(`${formData.dataNascimento}T12:00:00`).toISOString(),
        numeroMatricula: formData.numeroMatricula.trim(),
        horarioRealizacao: formData.horarioRealizacao.trim(),
        diasSemana: formData.diasSemana,
      });
      await popup.alert({
        icon: "success",
        title: "Aluno cadastrado com sucesso.",
        message:
          "Faça o download do termo de compromisso. Ele deve ser assinado e anexado ao sistema.",
        confirmText: "OK",
      });
      setView("lista");
      setFormData(null);
      await fetchAlunos();
    } catch (err) {
      await popup.alert({
        icon: "warning",
        title: "Erro ao salvar aluno",
        message: err.message || "Tente novamente.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (view === "form") {
    return (
      <FullWidthView>
        <CadastrarAlunoForm
          solicitacao={solicitacao}
          initialData={formData}
          onProximo={handleProximo}
          onVoltar={() => setView("lista")}
        />
      </FullWidthView>
    );
  }

  if (view === "revisao") {
    return (
      <FullWidthView>
        <CadastrarAlunoRevisao
          solicitacao={solicitacao}
          formData={formData}
          saving={saving}
          onVoltar={() => setView("form")}
          onSalvar={handleSalvar}
        />
      </FullWidthView>
    );
  }

  return (
    <Layout>
      <LeftColumn>
        <VagaResumoCard solicitacao={solicitacao} wide />
        <BottomArea>
          <GrayVoltarButton type="button" onClick={onVoltar}>
            Voltar
          </GrayVoltarButton>
        </BottomArea>
      </LeftColumn>

      <RightPanel>
          <PanelHeader>Alunos cadastrados</PanelHeader>
          <ProgressText>
            {alunos.length} de {total} alunos cadastrados
          </ProgressText>

          {loading && <ProgressText>Carregando...</ProgressText>}

          {!loading && (
            <SlotList>
              {slots.map((slot) => {
                if (slot.empty) {
                  return (
                    <SlotItem key={slot.id} $filled={false}>
                      <SlotHeader>
                        <SlotLeft>
                          <PersonIcon>person</PersonIcon>
                          <SlotTitle>Cadastrar aluno</SlotTitle>
                        </SlotLeft>
                        <AddButton type="button" onClick={handleOpenForm}>
                          +
                        </AddButton>
                      </SlotHeader>
                    </SlotItem>
                  );
                }

                const cpfDisplay = slot.cpf ? formatCpf(slot.cpf) : "-";
                const expanded = expandedIds[slot.id];

                return (
                  <SlotItem key={slot.id} $filled>
                    <SlotHeader>
                      <SlotLeft>
                        <PersonIcon>person</PersonIcon>
                        <div>
                          <SlotTitle $filled>{slot.nome}</SlotTitle>
                          <SlotSub>{cpfDisplay}</SlotSub>
                        </div>
                      </SlotLeft>
                      <SlotHeaderActions>
                        <TermoEnviadoStatus $sent={!!slot.termoCompromissoCaminho}>
                          {slot.termoCompromissoCaminho
                            ? "Termo enviado ✓"
                            : "Termo pendente ⚠"}
                        </TermoEnviadoStatus>
                        <ExpandButton type="button" onClick={() => toggleExpand(slot.id)}>
                          {expanded ? "▴" : "▾"}
                        </ExpandButton>
                      </SlotHeaderActions>
                    </SlotHeader>
                    {expanded && (
                      <SlotDetails>
                        <div>
                          <DetailLabel>Data de nascimento</DetailLabel>
                          <div>
                            {slot.dataNascimento
                              ? new Date(slot.dataNascimento).toLocaleDateString("pt-BR")
                              : "-"}
                          </div>
                        </div>
                        <div>
                          <DetailLabel>Matrícula</DetailLabel>
                          <div>{slot.numeroMatricula}</div>
                        </div>
                        <div>
                          <DetailLabel>Local</DetailLabel>
                          <div>{localRealizacao}</div>
                        </div>
                        <div>
                          <DetailLabel>Período</DetailLabel>
                          <div>
                            {inicioEstagio} — {terminoEstagio}
                          </div>
                        </div>
                        <div>
                          <DetailLabel>Horário</DetailLabel>
                          <div>{slot.horarioRealizacao}</div>
                        </div>
                        <div>
                          <DetailLabel>Carga horária</DetailLabel>
                          <div>{cargaHorariaTotal}h</div>
                        </div>
                        <div>
                          <DetailLabel>Dias da semana</DetailLabel>
                          <div>
                            {(slot.diasSemana || [])
                              .map((d) => d.diaSemana)
                              .join(", ") || "-"}
                          </div>
                        </div>
                        <GerarTermoCell>
                          <GerarTermoButton type="button" onClick={() => handleTermo(slot)}>
                            {slot.termoCompromissoCaminho
                              ? "Ver termo assinado"
                              : "Gerar termo"}
                          </GerarTermoButton>
                        </GerarTermoCell>
                      </SlotDetails>
                    )}
                  </SlotItem>
                );
              })}
            </SlotList>
          )}
        </RightPanel>
    </Layout>
  );
};

export default CadastrarAlunosDetalhe;
